import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, Terminal, SlidersHorizontal, Lock, PlayCircle, Wrench, Factory, Loader2 } from "lucide-react";
import { IndeterminateProgress } from "@/components/ui/circular-progress";
import { SearchHistoryDropdown } from "@/components/SearchHistoryDropdown";
import { useIsMobile } from "@/hooks/use-mobile";
import SearchProgress from "@/components/SearchProgress";
import RefSearchProgress from "@/components/RefSearchProgress";
import SearchResults from "@/components/SearchResults";
import SearchFilters from "@/components/SearchFilters";
import MobileSearchBar from "@/components/mobile/MobileSearchBar";
import MobileFilterDrawer from "@/components/mobile/MobileFilterDrawer";
import { SearchResult } from "@/types/search";
import { useToast } from "@/hooks/use-toast";
import { useScraperSettings } from "@/hooks/useScraperSettings";
import { useSearchMode } from "@/hooks/useSearchMode";
import { useSearchExport } from "@/hooks/useSearchExport";
import { useSearchTypeSettings } from "@/hooks/useSearchTypeSettings";
import { useDemoReferences } from "@/hooks/useDemoReferences";
import { getDemoResults, simulateSearchDelay } from "@/data/mockSearchData";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn, normalizeReference } from "@/lib/utils";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useSearchHistory } from "@/hooks/useSearchHistory";
import { useConsent } from "@/contexts/ConsentContext";
import TermsAcceptanceCheckbox from "@/components/TermsAcceptanceCheckbox";
import { useSearchPriceRecording } from "@/hooks/useSearchPriceRecording";
import { debugLog, debugWarn } from "@/contexts/DebugContext";
import { useManufacturerList } from "@/hooks/useManufacturerList";
import { ManufacturerSearch } from "@/components/ManufacturerSearch";

const ACTIVE_TAB_KEY = "partxplorer_active_tab";

const searchSchema = z.object({
  query: z.string()
    .trim()
    .min(1, { message: "Le champ de recherche ne peut pas être vide" })
    .max(50, { message: "La recherche doit faire moins de 50 caractères" })
    .regex(/^[a-zA-Z0-9\s\-_.]+$/, { message: "Caractères invalides détectés" }),
});

const SearchSection = () => {
  const isMobile = useIsMobile();
  const { scrapers } = useScraperSettings();
  const { settings: searchMode, isDisabled, isDemo } = useSearchMode();
  const { oemEnabled, refEnabled } = useSearchTypeSettings();
  const [activeTab, setActiveTab] = useState<"oem" | "ref">(() => {
    const saved = localStorage.getItem(ACTIVE_TAB_KEY);
    return (saved === "oem" || saved === "ref") ? saved : "oem";
  });
  const [oemNumber, setOemNumber] = useState("");
  const [manufacturerRef, setManufacturerRef] = useState("");
  const [selectedManufacturer, setSelectedManufacturer] = useState<string>("");
  
  // Auto-normalize references when user types
  const handleOemNumberChange = (value: string) => {
    const normalized = normalizeReference(value);
    setOemNumber(normalized);
  };
  
  const handleManufacturerRefChange = (value: string) => {
    const normalized = normalizeReference(value);
    setManufacturerRef(normalized);
  };
  const [isOemSearching, setIsOemSearching] = useState(false);
  const [isRefSearching, setIsRefSearching] = useState(false);
  const [oemResults, setOemResults] = useState<SearchResult[]>([]);
  const [refResults, setRefResults] = useState<SearchResult[]>([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([
    "Autodoc", "Oscaro", "Mister Auto", "Trodo", "Distriauto", "Carpardoo", "Motointegrator"
  ]);
  const [hideOutOfStock, setHideOutOfStock] = useState(true);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [selectedDemoRef, setSelectedDemoRef] = useState<string>("");
  const [searchProgress, setSearchProgress] = useState(0);
  const [triggerOemSearch, setTriggerOemSearch] = useState(false);
  const [triggerRefSearch, setTriggerRefSearch] = useState(false);
  const { toast } = useToast();
  const { data: demoReferences, isLoading: isDemoReferencesLoading } = useDemoReferences();
  const { addToHistory, getHistoryByType, removeItem, clearHistory } = useSearchHistory();
  const { hasAccepted, isLoading: isTermsLoading, acceptTerms } = useConsent();
  const [tempTermsAccepted, setTempTermsAccepted] = useState(false);
  const { recordPriceHistory } = useSearchPriceRecording();
  const { manufacturers } = useManufacturerList();

  useEffect(() => {
    localStorage.setItem(ACTIVE_TAB_KEY, activeTab);
  }, [activeTab]);

  const currentHistory = getHistoryByType(activeTab);
  const [isOemHistoryOpen, setIsOemHistoryOpen] = useState(false);
  const [isRefHistoryOpen, setIsRefHistoryOpen] = useState(false);
  
  // Trigger OEM search from history
  useEffect(() => {
    if (triggerOemSearch && oemNumber.trim()) {
      setTriggerOemSearch(false);
      handleOemSearch({ preventDefault: () => {} } as React.FormEvent);
    }
  }, [triggerOemSearch, oemNumber]);
  
  // Trigger Ref search from history
  useEffect(() => {
    if (triggerRefSearch && manufacturerRef.trim()) {
      setTriggerRefSearch(false);
      handleManufacturerSearch({ preventDefault: () => {} } as React.FormEvent);
    }
  }, [triggerRefSearch, manufacturerRef]);

  const handleFilterChange = (platforms: string[], hideStock: boolean, brands: string[]) => {
    setSelectedPlatforms(platforms);
    setHideOutOfStock(hideStock);
    setSelectedBrands(brands);
  };

  const handleBrandToggle = (brand: string) => {
    setSelectedBrands(prev => 
      prev.includes(brand) 
        ? prev.filter(b => b !== brand)
        : [...prev, brand]
    );
  };

  const handleSelectAllBrands = () => {
    const allBrands = Array.from(new Set(
      [...oemResults, ...refResults].map(r => r.brand).filter(Boolean)
    ));
    setSelectedBrands(allBrands as string[]);
  };

  const handleDeselectAllBrands = () => {
    setSelectedBrands([]);
  };

  // Auto-reset selected brands when results change (new search)
  useEffect(() => {
    setSelectedBrands([]);
  }, [oemResults, refResults]);

  const logSearch = async (searchQuery: string, searchType: "oem" | "ref", resultsCount: number) => {
    // Don't log searches in demo mode
    if (isDemo) return;
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      // Only log if user is authenticated
      if (!user) return;

      await supabase
        .from('search_logs')
        .insert({
          user_id: user.id,
          search_query: searchQuery,
          search_type: searchType,
          platforms: selectedPlatforms,
          results_count: resultsCount
        });
    } catch (error) {
      // Silently fail - search logging is not critical
    }
  };

  const handleOemSearch = (e: React.FormEvent) => {
    e.preventDefault();
    debugLog('[SearchSection] handleOemSearch called', { oemNumber, isDemo, isMobile });
    
    // Check terms acceptance first
    if (!hasAccepted && !tempTermsAccepted) {
      toast({
        variant: "destructive",
        title: "Acceptation requise",
        description: "Veuillez accepter les CGU et la Politique de Confidentialité avant de rechercher",
      });
      return;
    }
    
    // Save acceptance if temp accepted
    if (!hasAccepted && tempTermsAccepted) {
      acceptTerms();
    }
    
    const validation = searchSchema.safeParse({ query: oemNumber });
    if (!validation.success) {
      const errors = validation.error.errors.map(err => err.message).join(", ");
      debugWarn('[SearchSection] OEM validation failed', { errors });
      toast({
        variant: "destructive",
        title: "Validation échouée",
        description: errors,
      });
      return;
    }
    
    debugLog('[SearchSection] OEM validation ok, starting search');
    setSearchProgress(0);
    addToHistory(oemNumber, "oem");
    
    // Handle demo mode
    if (isDemo) {
      handleDemoSearch(oemNumber, "oem");
      return;
    }
    
    setIsOemSearching(true);
    setOemResults([]);
  };

  const handleOemSearchComplete = (results: SearchResult[]) => {
    setOemResults(results);
    setIsOemSearching(false);
    
    // Log the search
    logSearch(oemNumber, "oem", results.length);
    
    // Record price history for analytics
    recordPriceHistory(results, oemNumber, "oem");
    
    toast({
      title: "Recherche OEM terminée",
      description: `${results.length} résultats trouvés`,
    });
  };

  const handleOemSearchError = (error: string) => {
    setIsOemSearching(false);
    toast({
      title: "Erreur",
      description: error,
      variant: "destructive",
    });
  };

  const handleManufacturerSearch = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check manufacturer selection first
    if (!selectedManufacturer || selectedManufacturer.trim() === "") {
      toast({
        variant: "destructive",
        title: "Marque requise",
        description: "Veuillez sélectionner une marque avant de lancer la recherche",
      });
      return;
    }
    
    // Check terms acceptance
    if (!hasAccepted && !tempTermsAccepted) {
      toast({
        variant: "destructive",
        title: "Acceptation requise",
        description: "Veuillez accepter les CGU et la Politique de Confidentialité avant de rechercher",
      });
      return;
    }
    
    // Save acceptance if temp accepted
    if (!hasAccepted && tempTermsAccepted) {
      acceptTerms();
    }
    
    const validation = searchSchema.safeParse({ query: manufacturerRef });
    if (!validation.success) {
      const errors = validation.error.errors.map(err => err.message).join(", ");
      toast({
        variant: "destructive",
        title: "Validation échouée",
        description: errors,
      });
      return;
    }
    
    setSearchProgress(0);
    addToHistory(manufacturerRef, "ref");
    
    // Handle demo mode
    if (isDemo) {
      handleDemoSearch(manufacturerRef, "ref");
      return;
    }
    
    setIsRefSearching(true);
    setRefResults([]);
  };

  const handleRefSearchComplete = (results: SearchResult[]) => {
    setRefResults(results);
    setIsRefSearching(false);
    
    // Log the search
    logSearch(manufacturerRef, "ref", results.length);
    
    // Record price history for analytics
    recordPriceHistory(results, manufacturerRef, "ref");
    
    toast({
      title: "Recherche fabricant terminée",
      description: `${results.length} résultats trouvés`,
    });
  };

  const handleRefSearchError = (error: string) => {
    setIsRefSearching(false);
    toast({
      title: "Erreur",
      description: error,
      variant: "destructive",
    });
  };

  const handleCancelSearch = () => {
    setIsOemSearching(false);
    setIsRefSearching(false);
    setSearchProgress(0);
  };

  // Handle demo mode searches
  const handleDemoSearch = async (searchQuery: string, type: "oem" | "ref") => {
    if (type === "oem") {
      setOemNumber(searchQuery);
      setOemResults([]);
      setIsOemSearching(true);
      
      await simulateSearchDelay();
      const demoResults = await getDemoResults("oem", searchQuery);
      setOemResults(demoResults);
      setIsOemSearching(false);
      
      // Record price history for analytics (demo data)
      recordPriceHistory(demoResults, searchQuery, "oem");
      
      toast({
        title: "Recherche OEM terminée (Mode Démo)",
        description: `${demoResults.length} résultats trouvés`,
      });
    } else {
      setManufacturerRef(searchQuery);
      setRefResults([]);
      setIsRefSearching(true);
      
      await simulateSearchDelay();
      const demoResults = await getDemoResults("ref", searchQuery);
      setRefResults(demoResults);
      setIsRefSearching(false);
      
      // Record price history for analytics (demo data)
      recordPriceHistory(demoResults, searchQuery, "ref");
      
      toast({
        title: "Recherche Référence terminée (Mode Démo)",
        description: `${demoResults.length} résultats trouvés`,
      });
    }
  };

  const handleSelectAllPlatforms = () => {
    // Get all enabled scrapers from the database
    const allEnabled = scrapers.filter(s => s.enabled).map(s => s.scraper_name);
    setSelectedPlatforms(allEnabled);
  };

  const handleDeselectAllPlatforms = () => {
    setSelectedPlatforms([]);
  };

  const activeFiltersCount = 
    (hideOutOfStock ? 1 : 0) + 
    (selectedPlatforms.length < scrapers.filter(s => s.enabled).length ? 1 : 0);

  if (isMobile) {
    return (
      <section className="max-w-5xl mx-auto">
        <div className="space-y-6">
          {/* Status badge for disabled/demo mode */}
          {isDisabled && (
            <Alert className="border-destructive/50 bg-destructive/10">
              <Lock className="h-4 w-4" />
              <AlertDescription className="text-sm">
                {searchMode.disabledMessage}
              </AlertDescription>
            </Alert>
          )}
          {isDemo && (
            <>
              <Alert className="border-blue-500/50 bg-blue-500/10 py-2">
                <div className="flex items-center justify-center gap-2 w-full">
                  <PlayCircle className="h-3 w-3 text-blue-500 flex-shrink-0" />
                  <span className="text-xs leading-tight">
                    <strong>Mode Démo</strong> - Résultats à titre d'exemple
                  </span>
                </div>
              </Alert>
              
              {!isDemoReferencesLoading && demoReferences && demoReferences.length > 0 && (
                <div className="p-2 rounded-lg border border-border/50 bg-background/50 space-y-2">
                  <p className="text-xs font-medium text-foreground">
                    Références disponibles :
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {demoReferences.map((ref) => (
                      <Badge
                        key={`${ref.reference}-${ref.search_type}`}
                        variant="outline"
                        className="cursor-pointer hover:bg-primary/10 hover:border-primary transition-colors text-xs py-0.5 px-2"
                        onClick={() => {
                          if (ref.search_type === "oem") {
                            setOemNumber(ref.reference);
                            if (isDemo) {
                              handleDemoSearch(ref.reference, "oem");
                            } else {
                              handleOemSearch({ preventDefault: () => {} } as React.FormEvent);
                            }
                          } else {
                            setManufacturerRef(ref.reference);
                            if (isDemo) {
                              handleDemoSearch(ref.reference, "ref");
                            } else {
                              handleManufacturerSearch({ preventDefault: () => {} } as React.FormEvent);
                            }
                          }
                        }}
                      >
                        {ref.reference} <span className="text-[10px] text-muted-foreground ml-0.5">({ref.search_type === "oem" ? "OEM" : "REF"})</span>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
          
          <MobileSearchBar
            oemNumber={oemNumber}
            manufacturerRef={manufacturerRef}
            selectedManufacturer={selectedManufacturer}
            onOemChange={handleOemNumberChange}
            onRefChange={handleManufacturerRefChange}
            onManufacturerChange={setSelectedManufacturer}
            onOemSearch={isDemo ? () => handleDemoSearch(oemNumber, "oem") : handleOemSearch}
            onRefSearch={isDemo ? () => handleDemoSearch(manufacturerRef, "ref") : handleManufacturerSearch}
            isOemSearching={isOemSearching}
            isRefSearching={isRefSearching}
            onFilterClick={() => setIsFilterDrawerOpen(true)}
            activeFiltersCount={activeFiltersCount}
            disabled={isDisabled}
            searchProgress={searchProgress}
            isDemo={isDemo}
            getHistoryByType={getHistoryByType}
            onSelectHistoryItem={(query, type) => {
              if (type === "oem") {
                handleOemNumberChange(query);
                setIsOemHistoryOpen(false);
                setTriggerOemSearch(true);
              } else {
                handleManufacturerRefChange(query);
                setIsRefHistoryOpen(false);
                setTriggerRefSearch(true);
              }
            }}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            onRemoveHistoryItem={removeItem}
            onClearHistory={clearHistory}
            isOemHistoryOpen={isOemHistoryOpen}
            isRefHistoryOpen={isRefHistoryOpen}
            onOemFocus={() => setIsOemHistoryOpen(true)}
            onOemBlur={() => setTimeout(() => setIsOemHistoryOpen(false), 200)}
            onRefFocus={() => setIsRefHistoryOpen(true)}
            onRefBlur={() => setTimeout(() => setIsRefHistoryOpen(false), 200)}
            onCancelSearch={handleCancelSearch}
            manufacturers={manufacturers}
          />

          {/* Terms acceptance checkbox - show only if not accepted - Mobile */}
          {!hasAccepted && !isTermsLoading && (
            <div className="mt-4">
              <TermsAcceptanceCheckbox
                checked={tempTermsAccepted}
                onCheckedChange={setTempTermsAccepted}
              />
            </div>
          )}
          
          {/* SSE Progress components - hidden on mobile, visible on desktop */}
          <div className="hidden md:block">
            {isOemSearching && !isDemo && (
              <SearchProgress
                oem={oemNumber}
                onComplete={handleOemSearchComplete}
                onError={handleOemSearchError}
                onProgressUpdate={setSearchProgress}
              />
            )}

            {isRefSearching && !isDemo && (
              <RefSearchProgress
                refNumber={manufacturerRef}
                manufacturer={selectedManufacturer}
                onComplete={handleRefSearchComplete}
                onError={handleRefSearchError}
                onProgressUpdate={setSearchProgress}
              />
            )}
          </div>
          
          {/* Demo mode indicator - mobile */}
          {(isOemSearching || isRefSearching) && isDemo && (
            <div className="md:hidden animate-fade-in p-4 bg-background/50 rounded-lg border border-border/50 mt-3">
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <div className="h-2 w-2 bg-primary rounded-full animate-pulse" />
                Chargement des résultats de démonstration...
              </div>
            </div>
          )}
          
          {/* Results display on mobile */}
          
          {!isOemSearching && oemResults.length > 0 && (
            <div className="animate-scale-in">
              <SearchResults
                results={oemResults}
                selectedPlatforms={selectedPlatforms}
                hideOutOfStock={hideOutOfStock}
                selectedBrands={selectedBrands}
                searchType="oem"
                searchQuery={oemNumber}
              />
            </div>
          )}
          
          {!isRefSearching && refResults.length > 0 && (
            <div className="animate-scale-in">
              <SearchResults
                results={refResults}
                selectedPlatforms={selectedPlatforms}
                hideOutOfStock={hideOutOfStock}
                selectedBrands={selectedBrands}
                searchType="ref"
                searchQuery={manufacturerRef}
              />
            </div>
          )}
          
          <MobileFilterDrawer
            selectedPlatforms={selectedPlatforms}
            hideOutOfStock={hideOutOfStock}
            selectedBrands={selectedBrands}
            availableResults={[...oemResults, ...refResults]}
            onPlatformToggle={(platformId) => {
              setSelectedPlatforms(prev =>
                prev.includes(platformId)
                  ? prev.filter(p => p !== platformId)
                  : [...prev, platformId]
              );
            }}
            onHideOutOfStockToggle={() => setHideOutOfStock(prev => !prev)}
            onBrandToggle={handleBrandToggle}
            onSelectAllBrands={handleSelectAllBrands}
            onDeselectAllBrands={handleDeselectAllBrands}
            onSelectAll={handleSelectAllPlatforms}
            onDeselectAll={handleDeselectAllPlatforms}
            isOpen={isFilterDrawerOpen}
            onOpenChange={setIsFilterDrawerOpen}
          />
        </div>
      </section>
    );
  }

  // Desktop view - separate tabs
  return (
    <section className="max-w-5xl mx-auto">
      <div className="flex items-center gap-4 mb-8 md:mb-10">
        <div className="h-1.5 w-12 md:w-16 bg-gradient-to-r from-primary to-primary-glow rounded-full"></div>
        <Terminal className="w-6 h-6 md:w-7 md:h-7 text-primary" />
        <h2 className="text-2xl md:text-3xl font-bold font-mono uppercase tracking-wide">Recherche</h2>
        {isDemo && (
          <Badge className="ml-auto bg-blue-500 hover:bg-blue-600">
            <PlayCircle className="h-3 w-3 mr-1" />
            Mode Démo
          </Badge>
        )}
      </div>

      {/* Status alert for disabled mode */}
      {isDisabled && (
        <Alert className="mb-6 border-destructive/50 bg-destructive/10">
          <Lock className="h-4 w-4" />
          <AlertDescription>
            {searchMode.disabledMessage}
          </AlertDescription>
        </Alert>
      )}

      {/* Demo mode references */}
      {isDemo && !isDemoReferencesLoading && demoReferences && demoReferences.length > 0 && (
        <div className="mb-6 p-4 rounded-lg border border-border/50 bg-background/50 space-y-3">
          <p className="text-sm font-medium text-foreground font-mono">
            Références disponibles :
          </p>
          <div className="flex flex-wrap gap-2">
            {demoReferences.map((ref) => (
              <Badge
                key={`${ref.reference}-${ref.search_type}`}
                variant="outline"
                className="cursor-pointer hover:bg-primary/10 hover:border-primary transition-colors text-sm py-1 px-3 font-mono"
                onClick={() => {
                  if (ref.search_type === "oem") {
                    handleOemNumberChange(ref.reference);
                    handleDemoSearch(ref.reference, "oem");
                  } else {
                    handleManufacturerRefChange(ref.reference);
                    handleDemoSearch(ref.reference, "ref");
                  }
                }}
              >
                {ref.reference} <span className="text-xs text-muted-foreground ml-1">({ref.search_type === "oem" ? "OEM" : "REF"} • {ref.count} produits)</span>
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Unified search section with glass effect */}
      <div className="glass-effect rounded-xl p-6 shadow-xl">
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as "oem" | "ref")} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="oem" disabled={!oemEnabled} className="font-mono disabled:opacity-50 disabled:cursor-not-allowed">
              <Wrench className="w-4 h-4" />
              Recherche OEM
              {!oemEnabled && <span className="ml-2 text-xs">(Désactivée)</span>}
            </TabsTrigger>
            <TabsTrigger value="ref" disabled={!refEnabled} className="font-mono disabled:opacity-50 disabled:cursor-not-allowed">
              <Factory className="w-4 h-4" />
              Référence Fabricant
              {!refEnabled && <span className="ml-2 text-xs">(Désactivée)</span>}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="oem" className="animate-fade-in">
            <form onSubmit={handleOemSearch} className="space-y-4">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-primary transition-colors pointer-events-none" />
                <Input
                  type="search"
                  value={oemNumber}
                  onChange={(e) => handleOemNumberChange(e.target.value)}
                  onFocus={() => setIsOemHistoryOpen(true)}
                  onBlur={() => setTimeout(() => setIsOemHistoryOpen(false), 200)}
                  placeholder="Ex: 1K0615301 - Entrez un numéro OEM constructeur"
                  className="pl-12 pr-4 font-mono h-14 text-base border-2 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all hover:scale-[1.01]"
                  disabled={isDisabled}
                />
                <SearchHistoryDropdown
                  history={getHistoryByType("oem")}
                  onSelectItem={(query) => {
                    handleOemNumberChange(query);
                    setIsOemHistoryOpen(false);
                    setTimeout(() => {
                      const forms = document.querySelectorAll('form');
                      const oemForm = forms[0];
                      if (oemForm) {
                        const event = new Event('submit', { bubbles: true, cancelable: true });
                        oemForm.dispatchEvent(event);
                      }
                    }, 100);
                  }}
                  onRemoveItem={removeItem}
                  onClearAll={clearHistory}
                  isVisible={isOemHistoryOpen}
                  searchType="oem"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full h-14 font-mono font-bold uppercase tracking-wider text-base bg-gradient-to-r from-primary to-primary-glow hover:shadow-lg hover:scale-[1.02] transition-all duration-300"
                disabled={!oemNumber.trim() || isOemSearching || isDisabled}
              >
                {isOemSearching ? (
                  <span className="flex items-center gap-3">
                    <IndeterminateProgress size={20} strokeWidth={2.5} />
                    <span className="tracking-wide">Recherche en cours...</span>
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Search className="h-4 w-4" />
                    Rechercher
                  </span>
                )}
              </Button>
        </form>
      </TabsContent>

          <TabsContent value="ref" className="animate-fade-in">
            <form onSubmit={handleManufacturerSearch} className="space-y-4">
              {/* Manufacturer selection */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground flex items-center gap-2">
                  Marque <span className="text-destructive">*</span>
                </label>
                <ManufacturerSearch
                  manufacturers={manufacturers}
                  value={selectedManufacturer}
                  onChange={setSelectedManufacturer}
                  disabled={isRefSearching || isDisabled}
                  className="w-full"
                />
              </div>

              {/* Reference input */}
              <div className="space-y-2 relative">
                <label className="text-sm font-medium text-foreground flex items-center gap-2">
                  Référence <span className="text-destructive">*</span>
                </label>
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-primary transition-colors pointer-events-none" />
                  <Input
                    type="search"
                    value={manufacturerRef}
                    onChange={(e) => handleManufacturerRefChange(e.target.value)}
                    onFocus={() => setIsRefHistoryOpen(true)}
                    onBlur={() => setTimeout(() => setIsRefHistoryOpen(false), 200)}
                    placeholder={selectedManufacturer ? "Ex: 0986479B05" : "Sélectionnez d'abord une marque"}
                    className={cn(
                      "pl-12 pr-4 font-mono h-14 text-base border-2 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all hover:scale-[1.01]",
                      !selectedManufacturer && "cursor-not-allowed opacity-60"
                    )}
                    disabled={!selectedManufacturer || isRefSearching || isDisabled}
                  />
                </div>
                <SearchHistoryDropdown
                  history={getHistoryByType("ref")}
                  onSelectItem={(query) => {
                    handleManufacturerRefChange(query);
                    setIsRefHistoryOpen(false);
                    setTimeout(() => {
                      const forms = document.querySelectorAll('form');
                      const refForm = forms[1];
                      if (refForm) {
                        const event = new Event('submit', { bubbles: true, cancelable: true });
                        refForm.dispatchEvent(event);
                      }
                    }, 100);
                  }}
                  onRemoveItem={removeItem}
                  onClearAll={clearHistory}
                  isVisible={isRefHistoryOpen}
                  searchType="ref"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full h-14 font-mono font-bold uppercase tracking-wider text-base bg-gradient-to-r from-primary to-primary-glow hover:shadow-lg hover:scale-[1.02] transition-all duration-300"
                disabled={!manufacturerRef.trim() || !selectedManufacturer || isRefSearching || isDisabled}
              >
                {isRefSearching ? (
                  <span className="flex items-center gap-3">
                    <IndeterminateProgress size={20} strokeWidth={2.5} />
                    <span className="tracking-wide">Recherche en cours...</span>
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Search className="h-4 w-4" />
                    Rechercher
                  </span>
                )}
              </Button>
        </form>
      </TabsContent>
    </Tabs>
      </div>

      {/* Terms acceptance checkbox - show only if not accepted */}
      {!hasAccepted && !isTermsLoading && (
        <div className="mt-6">
          <TermsAcceptanceCheckbox
            checked={tempTermsAccepted}
            onCheckedChange={setTempTermsAccepted}
          />
        </div>
      )}

      {/* Filters section - Desktop only, integrated design */}
      <div className="hidden md:block mt-8">
        <SearchFilters 
          onFilterChange={handleFilterChange}
          availableResults={activeTab === "oem" ? oemResults : refResults}
        />
      </div>

      {/* Filter drawer - used by filter buttons */}
      <MobileFilterDrawer
        selectedPlatforms={selectedPlatforms}
        hideOutOfStock={hideOutOfStock}
        selectedBrands={selectedBrands}
        availableResults={activeTab === "oem" ? oemResults : refResults}
        onPlatformToggle={(platformId) => {
          setSelectedPlatforms(prev =>
            prev.includes(platformId)
              ? prev.filter(p => p !== platformId)
              : [...prev, platformId]
          );
        }}
        onHideOutOfStockToggle={() => setHideOutOfStock(prev => !prev)}
        onBrandToggle={handleBrandToggle}
        onSelectAllBrands={handleSelectAllBrands}
        onDeselectAllBrands={handleDeselectAllBrands}
        onSelectAll={handleSelectAllPlatforms}
        onDeselectAll={handleDeselectAllPlatforms}
        isOpen={isFilterDrawerOpen}
        onOpenChange={setIsFilterDrawerOpen}
      />

      {/* Search progress and results - SSE progress components handle API calls */}
      {isOemSearching && !isDemo && (
        <div className="animate-fade-in mt-6">
          <SearchProgress
            oem={oemNumber}
            onComplete={handleOemSearchComplete}
            onError={handleOemSearchError}
            onProgressUpdate={setSearchProgress}
          />
        </div>
      )}

      {isRefSearching && !isDemo && (
        <div className="animate-fade-in mt-6">
          <RefSearchProgress
            refNumber={manufacturerRef}
            manufacturer={selectedManufacturer}
            onComplete={handleRefSearchComplete}
            onError={handleRefSearchError}
            onProgressUpdate={setSearchProgress}
          />
        </div>
      )}

      {(isOemSearching || isRefSearching) && isDemo && (
        <div className="animate-fade-in mt-6 p-6 tech-border">
          <div className="flex items-center gap-3 text-sm text-muted-foreground font-mono">
            <div className="h-2 w-2 bg-blue-500 rounded-full animate-pulse" />
            Chargement des résultats de démonstration...
          </div>
        </div>
      )}

      {!isOemSearching && oemResults.length > 0 && (
        <div className="animate-scale-in mt-6">
          <SearchResults
            results={oemResults}
            selectedPlatforms={selectedPlatforms}
            hideOutOfStock={hideOutOfStock}
            selectedBrands={selectedBrands}
            searchType="oem"
            searchQuery={oemNumber}
          />
        </div>
      )}

      {!isRefSearching && refResults.length > 0 && (
        <div className="animate-scale-in mt-6">
          <SearchResults
            results={refResults}
            selectedPlatforms={selectedPlatforms}
            hideOutOfStock={hideOutOfStock}
            selectedBrands={selectedBrands}
            searchType="ref"
            searchQuery={manufacturerRef}
          />
        </div>
      )}
      
      <MobileFilterDrawer
        selectedPlatforms={selectedPlatforms}
        hideOutOfStock={hideOutOfStock}
        selectedBrands={selectedBrands}
        availableResults={[...oemResults, ...refResults]}
        onPlatformToggle={(platformId) => {
          setSelectedPlatforms(prev =>
            prev.includes(platformId)
              ? prev.filter(p => p !== platformId)
              : [...prev, platformId]
          );
        }}
        onHideOutOfStockToggle={() => setHideOutOfStock(prev => !prev)}
        onBrandToggle={handleBrandToggle}
        onSelectAllBrands={handleSelectAllBrands}
        onDeselectAllBrands={handleDeselectAllBrands}
        onSelectAll={handleSelectAllPlatforms}
        onDeselectAll={handleDeselectAllPlatforms}
        isOpen={isFilterDrawerOpen}
        onOpenChange={setIsFilterDrawerOpen}
      />
    </section>
  );
};

export default SearchSection;
